import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Trophy, Star, Clock, AlertCircle, Calendar, Search, CheckCircle2, XCircle } from "lucide-react";

const NIVEAU_CONFIG = {
  premium: {
    color: "text-accent",
    bgColor: "bg-accent/5",
    borderColor: "border-accent/20",
    icon: Trophy,
    title: "Profil PREMIUM",
    description: "Vous êtes un repreneur hautement qualifié. Vous avez accès prioritaire aux opportunités.",
    access: true,
    benefits: [
      "Accès complet au catalogue d'opportunités",
      "Contact prioritaire sous 24h",
      "Accompagnement dédié",
    ],
  },
  qualifie: {
    color: "text-primary",
    bgColor: "bg-primary/5",
    borderColor: "border-primary/20",
    icon: Star,
    title: "Profil QUALIFIÉ",
    description: "Votre profil est solide. Vous avez accès aux opportunités correspondant à vos critères.",
    access: true,
    benefits: [
      "Accès au catalogue d'opportunités",
      "Inscription liste prioritaire",
      "Alertes par email",
    ],
  },
  en_construction: {
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    icon: Clock,
    title: "Profil EN CONSTRUCTION",
    description: "Votre projet avance ! Quelques éléments restent à consolider pour accéder aux opportunités.",
    access: false,
    benefits: [
      "Accès aux ressources de préparation",
      "Newsletter mensuelle",
      "Relance dans 3 mois",
    ],
    improvements: [
      "Augmenter votre apport personnel",
      "Obtenir un pré-accord bancaire",
      "Définir un horizon de reprise plus court",
    ],
  },
  curieux: {
    color: "text-gray-600",
    bgColor: "bg-gray-50",
    borderColor: "border-gray-200",
    icon: AlertCircle,
    title: "Profil CURIEUX",
    description: "Vous êtes en phase d'exploration. Prenez le temps de mûrir votre projet avant de vous lancer.",
    access: false,
    benefits: [
      "Newsletter informative",
      "Guides et ressources gratuits",
    ],
    improvements: [
      "Constituer un apport plus conséquent",
      "Acquérir de l'expérience managériale",
      "Définir un projet plus concret",
      "Contacter votre banque pour un pré-accord",
    ],
  },
};

export default function RepreneurResult() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("repreneur_result");
    if (stored) {
      setResult(JSON.parse(stored));
    } else {
      navigate("/repreneur");
    }
  }, [navigate]);

  if (!result) {
    return null;
  }

  const config = NIVEAU_CONFIG[result.niveau] || NIVEAU_CONFIG.curieux;
  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-background py-16 px-8" data-testid="repreneur-result">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-16">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-foreground/60 hover:text-primary transition-colors font-medium"
            data-testid="back-home"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </button>
          <span className="text-3xl font-bold text-primary">Sequenti</span>
        </div>

        {/* Score Card */}
        <Card className={`p-12 mb-12 ${config.bgColor} ${config.borderColor} border-none rounded-lg shadow-sm border border-border/40 relative overflow-hidden`}>
            <div className={`absolute top-0 left-0 w-full h-2 ${config.color === 'text-accent' ? 'bg-accent' : (config.color === 'text-primary' ? 'bg-primary' : 'bg-orange-500')}`} />
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-24 h-24 ${config.bgColor} rounded-full mb-8 border-2 ${config.borderColor}`}>
              <Icon className={`w-12 h-12 ${config.color}`} />
            </div>
            <h1 className={`text-4xl md:text-5xl font-black ${config.color} mb-4`}>
              {config.title}
            </h1>
            <p className="text-xl text-primary font-bold mb-8">
              {result.prenom} {result.nom}
            </p>
            
            {/* Score Display */}
            <div className="max-w-md mx-auto mb-8 bg-white/50 p-6 rounded-lg backdrop-blur-sm">
              <div className="flex justify-between items-center text-sm font-bold uppercase tracking-widest mb-3">
                <span className="text-foreground/40">Score de qualification</span>
                <span className={`${config.color}`}>{result.score}/100</span>
              </div>
              <Progress value={result.score} className="h-4 bg-muted" />
            </div>

            <p className="text-lg text-foreground/70 max-w-xl mx-auto leading-relaxed">
              {config.description}
            </p>
          </div>
        </Card>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Benefits */}
            <Card className="p-10 border-none bg-white shadow-sm border border-border/40">
            <h2 className="text-2xl font-bold text-primary mb-8 flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-accent" />
                Ce que vous obtenez
            </h2>
            <ul className="space-y-4">
                {config.benefits.map((benefit, index) => (
                <li key={index} className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-accent rounded-full" />
                    <span className="text-lg text-foreground/70 font-medium">{benefit}</span>
                </li>
                ))}
            </ul>
            </Card>

            {/* Improvements (for non-qualified) */}
            {config.improvements ? (
            <Card className="p-10 border-none bg-orange-50/30 shadow-sm border border-orange-100">
                <h2 className="text-2xl font-bold text-orange-700 mb-8 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-orange-600" />
                Pour améliorer votre score
                </h2>
                <ul className="space-y-4">
                {config.improvements.map((improvement, index) => (
                    <li key={index} className="flex items-start gap-4">
                        <XCircle className="w-5 h-5 text-orange-500 mt-1 shrink-0" />
                        <span className="text-lg text-orange-800/70 font-medium">{improvement}</span>
                    </li>
                ))}
                </ul>
            </Card>
            ) : (
                <Card className="p-10 border-none bg-primary/5 shadow-sm border border-primary/10 flex flex-col items-center justify-center text-center">
                    <Trophy className="w-16 h-16 text-accent mb-6" />
                    <p className="text-xl font-bold text-primary italic">Profil hautement valorisé par les cédants.</p>
                </Card>
            )}
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row gap-6 justify-center mt-16">
          {config.access ? (
            <>
              <Button
                size="lg"
                className="bg-accent text-white hover:bg-accent/90 text-xl h-16 px-10 rounded-full shadow-lg transition-transform hover:scale-105"
                onClick={() => {
                  localStorage.setItem("repreneur_id", result.id);
                  navigate("/opportunites");
                }}
                data-testid="opportunites-button"
              >
                <Search className="w-6 h-6 mr-3" />
                Voir les opportunités
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-primary border-primary/20 hover:bg-muted text-xl h-16 px-10 rounded-full transition-transform hover:scale-105"
                onClick={() => window.open("https://calendly.com/eric-sequenti/30min", "_blank")}
                data-testid="rdv-button"
              >
                <Calendar className="w-6 h-6 mr-3" />
                Prendre rendez-vous
              </Button>
            </>
          ) : (
            <>
              <Button
                size="lg"
                className="bg-accent text-white hover:bg-accent/90 text-xl h-16 px-10 rounded-full shadow-lg transition-transform hover:scale-105"
                onClick={() => window.open("https://calendly.com/eric-sequenti/30min", "_blank")}
                data-testid="rdv-button"
              >
                <Calendar className="w-6 h-6 mr-3" />
                Discuter de mon projet
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-primary border-primary/20 hover:bg-muted text-xl h-16 px-10 rounded-full transition-transform hover:scale-105"
                onClick={() => navigate("/")}
                data-testid="home-button"
              >
                Retour à l'accueil
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
